import { useMemo, useState } from 'react';
import {
  RECLASSIFY_REASONS,
  type Account,
  type JournalEntryWithLines,
  type ReclassifyReasonCode,
} from '@accounting/shared';
import { AccountPicker } from '../accounts/AccountPicker';
import { useReclassifyJournalEntry } from '../../hooks/useJournalEntries';
import { ApiError } from '../../lib/api';
import { formatCurrency } from '../../lib/formatters';

interface DraftLine {
  key: string;
  accountId: string;
  side: 'debit' | 'credit';
  amount: string;
}

/**
 * Re-account an entry without unwinding its reconciliation.
 *
 * The reconciled bank line is rendered as a frozen, uneditable row — showing it
 * rather than hiding it is deliberate: it is the thing that makes this safe,
 * and seeing it sit unchanged is what tells you the reconciliation survives.
 * Only the remaining lines are editable, and they must re-balance against it.
 */
export function ReclassifyModal({
  entry,
  accounts,
  frozenAccountId,
  onClose,
}: {
  entry: JournalEntryWithLines;
  accounts: Account[];
  /** GL account of the reconciliation that locked this entry; null if unlocked. */
  frozenAccountId: string | null;
  onClose: () => void;
}) {
  const reclassify = useReclassifyJournalEntry(entry.companyId);

  const frozenLines = useMemo(
    () => (frozenAccountId ? entry.lines.filter((l) => l.accountId === frozenAccountId) : []),
    [entry.lines, frozenAccountId],
  );

  const [lines, setLines] = useState<DraftLine[]>(() =>
    entry.lines
      .filter((l) => l.accountId !== frozenAccountId)
      .map((l, i) => {
        const debit = parseFloat(l.debit);
        return {
          key: `${l.id}-${i}`,
          accountId: l.accountId,
          side: debit > 0 ? ('debit' as const) : ('credit' as const),
          amount: (debit > 0 ? debit : parseFloat(l.credit)).toFixed(2),
        };
      }),
  );
  const [reasonCode, setReasonCode] = useState<ReclassifyReasonCode>('misclassified');
  const [reasonNote, setReasonNote] = useState('');

  const accountById = useMemo(() => new Map(accounts.map((a) => [a.id, a])), [accounts]);

  const setLine = (key: string, patch: Partial<DraftLine>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const addLine = () =>
    setLines((prev) => [
      ...prev,
      { key: `new-${Date.now()}-${prev.length}`, accountId: '', side: 'debit', amount: '' },
    ]);
  const removeLine = (key: string) => setLines((prev) => prev.filter((l) => l.key !== key));

  /**
   * Split an editable line into two. The workhorse for the payroll case: take
   * one "Payroll Expenses" line and break it apart without retyping the rest.
   */
  const splitLine = (key: string) =>
    setLines((prev) => {
      const idx = prev.findIndex((l) => l.key === key);
      if (idx < 0) return prev;
      const src = prev[idx]!;
      const copy = { ...src, key: `split-${Date.now()}-${idx}`, accountId: '', amount: '' };
      return [...prev.slice(0, idx + 1), copy, ...prev.slice(idx + 1)];
    });

  const num = (s: string) => {
    const v = parseFloat(s);
    return Number.isFinite(v) ? v : 0;
  };

  const frozenDebits = frozenLines.reduce((s, l) => s + parseFloat(l.debit), 0);
  const frozenCredits = frozenLines.reduce((s, l) => s + parseFloat(l.credit), 0);
  const draftDebits = lines.reduce((s, l) => s + (l.side === 'debit' ? num(l.amount) : 0), 0);
  const draftCredits = lines.reduce((s, l) => s + (l.side === 'credit' ? num(l.amount) : 0), 0);
  const totalDebits = frozenDebits + draftDebits;
  const totalCredits = frozenCredits + draftCredits;
  const difference = totalDebits - totalCredits;
  const balanced = Math.abs(difference) < 0.005;

  const everyLineComplete = lines.every((l) => l.accountId && num(l.amount) > 0);
  const noteRequired = reasonCode === 'other';
  const canSubmit =
    balanced &&
    everyLineComplete &&
    lines.length > 0 &&
    (!noteRequired || reasonNote.trim().length > 0) &&
    !reclassify.isPending;

  const submit = () => {
    if (!canSubmit) return;
    const payload = [
      ...frozenLines.map((l) => ({
        accountId: l.accountId,
        debit: parseFloat(l.debit).toFixed(4),
        credit: parseFloat(l.credit).toFixed(4),
      })),
      ...lines.map((l) => ({
        accountId: l.accountId,
        debit: l.side === 'debit' ? num(l.amount).toFixed(4) : '0.0000',
        credit: l.side === 'credit' ? num(l.amount).toFixed(4) : '0.0000',
      })),
    ];
    reclassify.mutate(
      {
        entryId: entry.id,
        input: {
          lines: payload,
          reasonCode,
          reasonNote: reasonNote.trim() || null,
        },
      },
      { onSuccess: onClose },
    );
  };

  const errorMessage =
    reclassify.error instanceof ApiError
      ? (reclassify.error.body as { error?: string } | null)?.error ?? 'Reclassification failed'
      : reclassify.error?.message;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginTop: 0, fontSize: 20 }}>Reclassify {entry.entryNumber}</h2>
        <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
          {frozenAccountId ? (
            <>
              This entry is reconciled. The money movement is settled and stays exactly as it is —
              you are only changing which accounts the other side lands in, so the reconciliation
              and its audit trail remain intact.
            </>
          ) : (
            <>
              Change which accounts this entry posts to. The date and entry number stay the same.
            </>
          )}
        </p>

        {frozenLines.length > 0 && (
          <div
            className="card accent-bank"
            style={{ padding: 12, marginBottom: 12, background: 'var(--bg-surface-2)' }}
          >
            <div
              style={{
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'var(--text-faint)',
                marginBottom: 6,
              }}
            >
              Locked by reconciliation — cannot change
            </div>
            {frozenLines.map((l) => (
              <div
                key={l.id}
                style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 14 }}
              >
                <span>{accountById.get(l.accountId)?.name ?? l.accountId}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {parseFloat(l.debit) > 0
                    ? `DR ${formatCurrency(l.debit)}`
                    : `CR ${formatCurrency(l.credit)}`}
                </span>
              </div>
            ))}
          </div>
        )}

        <table className="table" style={{ marginBottom: 12 }}>
          <thead>
            <tr>
              <th>Account</th>
              <th style={{ width: 110 }}>Side</th>
              <th className="right" style={{ width: 140 }}>
                Amount
              </th>
              <th style={{ width: 120 }} />
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.key}>
                <td>
                  <AccountPicker
                    accounts={accounts}
                    value={l.accountId}
                    onChange={(accountId) => setLine(l.key, { accountId })}
                    predicate={(a) => a.isActive && a.allowDirectPosting}
                    style={{ width: '100%' }}
                  />
                </td>
                <td>
                  <select
                    value={l.side}
                    onChange={(e) =>
                      setLine(l.key, { side: e.target.value as 'debit' | 'credit' })
                    }
                    aria-label="Debit or credit"
                  >
                    <option value="debit">Debit</option>
                    <option value="credit">Credit</option>
                  </select>
                </td>
                <td className="right">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={l.amount}
                    onChange={(e) => setLine(l.key, { amount: e.target.value })}
                    style={{ width: 120, textAlign: 'right' }}
                    aria-label="Amount"
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="btn-secondary"
                    style={{ padding: '4px 8px', fontSize: 12 }}
                    onClick={() => splitLine(l.key)}
                    title="Add another line below, to split this amount"
                  >
                    Split
                  </button>{' '}
                  {lines.length > 1 && (
                    <button
                      type="button"
                      className="btn-danger"
                      style={{ padding: '4px 8px', fontSize: 12 }}
                      onClick={() => removeLine(l.key)}
                    >
                      ✕
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <button type="button" className="btn-secondary" onClick={addLine}>
          Add line
        </button>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 24,
            margin: '14px 0',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <span className="muted">Debits {formatCurrency(totalDebits.toFixed(2))}</span>
          <span className="muted">Credits {formatCurrency(totalCredits.toFixed(2))}</span>
          <strong style={{ color: balanced ? 'var(--ok)' : 'var(--danger)' }}>
            {balanced
              ? 'Balanced'
              : `Out by ${formatCurrency(Math.abs(difference).toFixed(2))}`}
          </strong>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
          <label style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Reason for the change (recorded in the audit log)
          </label>
          <select
            value={reasonCode}
            onChange={(e) => setReasonCode(e.target.value as ReclassifyReasonCode)}
          >
            {RECLASSIFY_REASONS.map((r) => (
              <option key={r.code} value={r.code}>
                {r.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={reasonNote}
            onChange={(e) => setReasonNote(e.target.value)}
            placeholder={noteRequired ? 'Required — describe the change' : 'Optional note'}
            maxLength={500}
          />
        </div>

        {errorMessage && <p className="field-error">{errorMessage}</p>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={submit} disabled={!canSubmit}>
            {reclassify.isPending ? 'Saving…' : 'Reclassify'}
          </button>
        </div>
      </div>
    </div>
  );
}
